import { useEffect, useRef, useState } from "react";

const CHAR_DELAY_MS = 30;

export const useTypingAnimation = (oldText: string, newText: string, shouldAnimate: boolean) => {
  const [displayedText, setDisplayedText] = useState(oldText);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (!shouldAnimate || oldText === newText) {
      setDisplayedText(newText);
      setIsAnimating(false);

      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      return;
    }

    setIsAnimating(true);
    let currentStep = 0;
    let lastUpdateTime = performance.now();
    const deleteSteps = oldText.length;
    const typeSteps = newText.length;
    const totalSteps = deleteSteps + typeSteps;

    const animate = (timestamp: number) => {
      const elapsed = timestamp - lastUpdateTime;

      if (elapsed >= CHAR_DELAY_MS) {
        if (currentStep < deleteSteps) {
          const charsToShow = oldText.length - currentStep - 1;
          setDisplayedText(oldText.substring(0, charsToShow));
        } else {
          const charsToShow = currentStep - deleteSteps + 1;
          setDisplayedText(newText.substring(0, charsToShow));
        }

        currentStep = currentStep + 1;
        lastUpdateTime = timestamp;
      }

      if (currentStep < totalSteps) {
        animationFrameRef.current = window.requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        animationFrameRef.current = null;
      }
    };

    animationFrameRef.current = window.requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [oldText, newText, shouldAnimate]);

  return { displayedText, isAnimating };
};
